import { createBaseApi } from '../baseApi';

const reviewApi = createBaseApi('reviewApi');

export const enhancedReviewApi = reviewApi.injectEndpoints({
  endpoints: (builder) => ({
    getProductReviews: builder.query({
      query: ({ productId, fulfillmentGroupId, page = 1, pageSize = 10, sort = 'createdAt:desc', strapiV3 = false }) => {
        if (strapiV3) {
          const v3Sort = sort.replace('createdAt', 'created_at');
          const params = {
            products: productId,
            _start: (page - 1) * pageSize,
            _limit: pageSize,
            _sort: v3Sort,
            _publicationState: 'live'
          };
          if (fulfillmentGroupId) {
            params.fulfillmentGroup = fulfillmentGroupId;
          }
          return {
            url: '/comments',
            params
          };
        } else {
          const filters = { 'products': { 'id': { '$eq': productId } } };
          if (fulfillmentGroupId) {
            filters.fulfillmentGroup = { 'id': { '$eq': fulfillmentGroupId } };
          }
          return {
            url: '/comments',
            params: {
              filters,
              'pagination[page]': page,
              'pagination[pageSize]': pageSize,
              sort,
              _publicationState: 'live'
            }
          };
        }
      },
      providesTags: (result, error, { productId, fulfillmentGroupId }) => [
        { type: 'Review', id: 'LIST' },
        { type: 'Review', id: `PRODUCT_${productId}` },
        ...(fulfillmentGroupId ? [{ type: 'Review', id: `FG_${fulfillmentGroupId}_PROD_${productId}` }] : [])
      ]
    }),
    getReviewStats: builder.query({
      query: (productId) => ({
        url: '/comments/stats',
        params: {
          'filters[products][id][$eq]': productId,
          _publicationState: 'live'
        }
      }),
      providesTags: (result, error, productId) => [
        { type: 'Review', id: `STATS_${productId}` }
      ]
    }),
    addReview: builder.mutation({
      query: (reviewData) => {
        const isFormData = reviewData instanceof FormData;
        if (isFormData) {
          return {
            url: '/comments',
            method: 'POST',
            body: reviewData,
            headers: {},
            formData: true 
          };
        }
        const payload = {
          rating: reviewData.rating,
          comment: reviewData.comment,
          products: reviewData.products || (reviewData.productId ? [parseInt(reviewData.productId, 10)] : []),
          customers: reviewData.customers || (reviewData.customerId ? [parseInt(reviewData.customerId, 10)] : []),
          merchants: reviewData.merchants || (reviewData.merchantId ? [parseInt(reviewData.merchantId, 10)] : []),
          orders: reviewData.orders || (reviewData.orderId ? [parseInt(reviewData.orderId, 10)] : []),
          fulfillmentGroup: reviewData.fulfillmentGroup || 
            (reviewData.fulfillmentGroupId ? parseInt(reviewData.fulfillmentGroupId, 10) : null),
          orderItem: reviewData.orderItem || undefined,
          pictures: reviewData.pictures,
          isReviewCustomer: reviewData.isReviewCustomer,
          isReviewProduct: reviewData.isReviewProduct
        };
        return {
        url: '/comments',
        method: 'POST',
          body: payload
        };
      },
      invalidatesTags: (result, error, arg) => {
        const tagsToInvalidate = [{ type: 'Review', id: 'LIST' }];
        let reviewDetails = {};

        if (arg instanceof FormData) {
          try {
            const dataStr = arg.get('data');
            if (dataStr) reviewDetails = JSON.parse(dataStr);
          } catch (e) {
            console.error('Error parsing FormData for invalidation in addReview:', e);
          }
        } else {
          reviewDetails = arg;
        }
        
        // Use the created review data (result) for more reliable IDs if available
        const finalReviewData = result || reviewDetails;

        const productId = finalReviewData.products?.[0]?.id || finalReviewData.products?.[0] || finalReviewData.productId;
        const isReviewCustomer = finalReviewData.isReviewCustomer;
        
        const authorId = isReviewCustomer 
          ? (finalReviewData.merchants?.[0]?.id || finalReviewData.merchants?.[0]) 
          : (finalReviewData.customers?.[0]?.id || finalReviewData.customers?.[0]);
        
        const recipientId = isReviewCustomer 
          ? (finalReviewData.customers?.[0]?.id || finalReviewData.customers?.[0])
          : (finalReviewData.merchants?.[0]?.id || finalReviewData.merchants?.[0]);

        if (productId) {
          tagsToInvalidate.push(
            { type: 'Review', id: `PRODUCT_${productId}` },
            { type: 'Review', id: `STATS_${productId}` }
          );
        }
        if (authorId) {
          tagsToInvalidate.push({ type: 'UserReviewsGiven', id: authorId });
        }
        if (recipientId) {
          tagsToInvalidate.push({ type: 'UserReviewsReceived', id: recipientId });
        }
        // Consider invalidating specific merchant review list if applicable
        // if (recipientId && !isReviewCustomer) { // Product review for a merchant
        //   tagsToInvalidate.push({ type: 'Review', id: `MERCHANT-${recipientId}-LIST` });
        // }
        return tagsToInvalidate;
      }
    }),
    updateReview: builder.mutation({
      query: ({ id, ...reviewData }) => {
        const isFormData = reviewData instanceof FormData;
        if (isFormData) {
          return {
            url: `/comments/${id}`,
            method: 'PUT',
            body: reviewData,
            headers: {},
            formData: true
          };
        }
        // Ensure IDs are integers if they come from reviewData directly (e.g. from a form state)
        const formatIdArray = (arr) => arr && arr.map(item => (typeof item === 'object' && item.id) ? item.id : parseInt(item,10)).filter(id => !isNaN(id));
        const formatId = (val) => (typeof val === 'object' && val.id) ? val.id : (val ? parseInt(val,10) : undefined);

        const payload = {
          rating: reviewData.rating,
          comment: reviewData.comment,
          products: reviewData.products ? formatIdArray(reviewData.products) : undefined,
          customers: reviewData.customers ? formatIdArray(reviewData.customers) : undefined,
          merchants: reviewData.merchants ? formatIdArray(reviewData.merchants) : undefined,
          orders: reviewData.orders ? formatIdArray(reviewData.orders) : undefined,
          fulfillmentGroup: reviewData.fulfillmentGroupId ? formatId(reviewData.fulfillmentGroupId) : 
                            (reviewData.fulfillmentGroup ? formatId(reviewData.fulfillmentGroup) : undefined),
          orderItem: reviewData.orderItem || undefined,
          pictures: reviewData.pictures,
          isReviewCustomer: reviewData.isReviewCustomer,
          isReviewProduct: reviewData.isReviewProduct
        };
        // Remove undefined fields to prevent issues with Strapi
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
        
        return {
        url: `/comments/${id}`,
        method: 'PUT',
          body: payload
        };
      },
      invalidatesTags: (result, error, { id, ...arg }) => {
        const tagsToInvalidate = [
          { type: 'Review', id },
          { type: 'Review', id: 'LIST' }
        ];
        
        // Use the updated review data (result) for more reliable IDs
        const finalReviewData = result || arg;

        const productId = finalReviewData.products?.[0]?.id || finalReviewData.products?.[0] || finalReviewData.productId;
        const isReviewCustomer = finalReviewData.isReviewCustomer;

        const authorId = isReviewCustomer 
          ? (finalReviewData.merchants?.[0]?.id || finalReviewData.merchants?.[0]) 
          : (finalReviewData.customers?.[0]?.id || finalReviewData.customers?.[0]);
        
        const recipientId = isReviewCustomer 
          ? (finalReviewData.customers?.[0]?.id || finalReviewData.customers?.[0])
          : (finalReviewData.merchants?.[0]?.id || finalReviewData.merchants?.[0]);

        if (productId) {
          tagsToInvalidate.push(
            { type: 'Review', id: `PRODUCT_${productId}` },
            { type: 'Review', id: `STATS_${productId}` }
          );
        }
        if (authorId) {
          tagsToInvalidate.push({ type: 'UserReviewsGiven', id: authorId });
        }
        if (recipientId) {
          tagsToInvalidate.push({ type: 'UserReviewsReceived', id: recipientId });
        }
        // if (recipientId && !isReviewCustomer) {
        //   tagsToInvalidate.push({ type: 'Review', id: `MERCHANT-${recipientId}-LIST` });
        // }
        return tagsToInvalidate;
      }
    }),
    deleteReview: builder.mutation({
      // Expect `originalReviewData` to be passed for robust invalidation
      query: ({ id }) => ({ 
        url: `/comments/${id}`,
        method: 'DELETE'
      }),
      // `originalReviewData` should ideally contain the review object before deletion
      // to correctly identify author, recipient, and type for tag invalidation.
      invalidatesTags: (result, error, { id, productId, originalReviewData }) => {
        const tags = [
        { type: 'Review', id },
          { type: 'Review', id: 'LIST' }
        ];
        if (productId) { // productId might still be useful for generic product page updates
            tags.push({ type: 'Review', id: `PRODUCT_${productId}` });
            tags.push({ type: 'Review', id: `STATS_${productId}` });
        }

        if (originalReviewData) {
          const isReviewCustomer = originalReviewData.isReviewCustomer;
          const authorId = isReviewCustomer 
            ? (originalReviewData.merchants?.[0]?.id || originalReviewData.merchants?.[0])
            : (originalReviewData.customers?.[0]?.id || originalReviewData.customers?.[0]);
          
          const recipientId = isReviewCustomer 
            ? (originalReviewData.customers?.[0]?.id || originalReviewData.customers?.[0])
            : (originalReviewData.merchants?.[0]?.id || originalReviewData.merchants?.[0]);

          if (authorId) {
            tags.push({ type: 'UserReviewsGiven', id: authorId });
          }
          if (recipientId) {
            tags.push({ type: 'UserReviewsReceived', id: recipientId });
          }
          // const reviewedProductId = originalReviewData.products?.[0]?.id || originalReviewData.products?.[0];
        } else {
          // Fallback: Invalidate all general user review lists if specific data is missing
          // This is a broader invalidation, less ideal but safer if originalReviewData isn't available.
        }
        return tags;
      }
    }),
    getUserReviewsGiven: builder.query({
      query: ({ userId, page = 1, pageSize = 10, sort = 'updated_at:DESC' }) => {
        const numUserId = parseInt(userId, 10);
        if (isNaN(numUserId)) {
          return { url: `/comments/user/${userId}/given`, params: { _limit: 0 } };
        }
          return {
          url: `/comments/user/${numUserId}/given`,
          params: {
            _sort: sort,
            _start: (page - 1) * pageSize,
            _limit: pageSize,
          },
        };
      },
      providesTags: (result, error, { userId }) => [{ type: 'UserReviewsGiven', id: userId }],
    }),
    getUserReviewsReceived: builder.query({
      query: ({ userId, page = 1, pageSize = 10, sort = 'updated_at:DESC' }) => {
        const numUserId = parseInt(userId, 10);
        if (isNaN(numUserId)) {
          return { url: `/comments/user/${userId}/received`, params: { _limit: 0 } };
        }
        return {
          url: `/comments/user/${numUserId}/received`,
          params: {
            _sort: sort,
            _start: (page - 1) * pageSize,
            _limit: pageSize,
          },
        };
      },
      providesTags: (result, error, { userId }) => [{ type: 'UserReviewsReceived', id: userId }],
    }),
    getReviewById: builder.query({
      query: (id) => ({ url: `/comments/${id}`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'Review', id }],
    }),
    getMerchantReviews: builder.query({ 
      query: ({ merchantId, page = 1, pageSize = 10, sort = 'updated_at:DESC' }) => {
        if (!merchantId) throw new Error('merchantId is required for getMerchantReviews');
        const numMerchantId = parseInt(merchantId, 10);
        const params = {
          'merchants.id': numMerchantId,
          'isReviewProduct': true, // Ensuring these are product reviews for the merchant using new field
          _start: (page - 1) * pageSize,
          _limit: pageSize,
          _sort: sort.replace('createdAt', 'created_at'), 
          _publicationState: 'live'
        };
        return { url: '/comments', params };
      },
      providesTags: (result, error, { merchantId }) => 
        result 
          ? [
              ...result.map(({ id }) => ({ type: 'Review', id: `MERCHANT-${merchantId}-PRODREVIEW-${id}` })),
              { type: 'UserReviewsReceived', id: merchantId }, 
              { type: 'Review', id: `MERCHANT-${merchantId}-LIST` } 
            ]
          : [
              { type: 'UserReviewsReceived', id: merchantId },
              { type: 'Review', id: `MERCHANT-${merchantId}-LIST` }
            ],
    }),
    getExistingCustomerReview: builder.query({
      query: ({ fulfillmentGroupId, reviewedCustomerId, authorMerchantId }) => ({
        url: '/comments',
        params: {
          fulfillmentGroup: fulfillmentGroupId,
          'customers.id': reviewedCustomerId,
          'merchants.id': authorMerchantId,
          isReviewCustomer: true,
          isReviewProduct: false, // Explicitly false for customer reviews
          _limit: 1,
          _sort: 'created_at:DESC',
        },
      }),
      transformResponse: (response) => response?.[0] || null,
      providesTags: (result, error, { fulfillmentGroupId, reviewedCustomerId, authorMerchantId }) => {
        const tags = [];
        if (result) {
          tags.push({ type: 'Review', id: result.id });
        }
        tags.push({ type: 'ExistingCustomerReview', id: `${authorMerchantId}-FOR-${reviewedCustomerId}-FG-${fulfillmentGroupId}` });
        if (authorMerchantId) tags.push({ type: 'UserReviewsGiven', id: authorMerchantId });
        if (reviewedCustomerId) tags.push({ type: 'UserReviewsReceived', id: reviewedCustomerId });
        return tags;
      }
    }),
    getExistingOrderCustomerReview: builder.query({
      query: ({ orderId, authorMerchantId }) => ({
        url: `/comments/order/${orderId}/customer-review`,
      }),
      transformResponse: (response) => {
        // Handle both array and single object responses, extract first item if array
        if (Array.isArray(response)) {
          return response[0] || null;
        }
        return response || null;
      },
      providesTags: (result, error, { orderId, authorMerchantId }) => {
        const tags = [];
        if (result) {
          tags.push({ type: 'Review', id: result.id });
          // Extract customer ID from the result for tagging
          const customerId = result.customers?.[0]?.id;
          if (customerId) {
            tags.push({ type: 'ExistingOrderCustomerReview', id: `${authorMerchantId}-FOR-${customerId}-ORDER-${orderId}` });
            tags.push({ type: 'UserReviewsReceived', id: customerId });
          }
        } else {
          // Even when no review exists, tag the pattern for cache invalidation
          tags.push({ type: 'ExistingOrderCustomerReview', id: `${authorMerchantId}-ORDER-${orderId}` });
        }
        if (authorMerchantId) tags.push({ type: 'UserReviewsGiven', id: authorMerchantId });
        return tags;
      }
    }),
    getExistingOrderItemProductReview: builder.query({
      query: ({ orderItemId }) => ({
        url: `/comments/order-item/${orderItemId}/product-review`,
      }),
      transformResponse: (response) => {
        // Handle both array and single object responses, extract first item if array
        if (Array.isArray(response)) {
          return response[0] || null;
        }
        return response || null;
      },
      providesTags: (result, error, { orderItemId }) => {
        const tags = [];
        if (result) {
          tags.push({ type: 'Review', id: result.id });
          // Extract customer ID from the result for tagging
          const customerId = result.customers?.[0]?.id;
          if (customerId) {
            tags.push({ type: 'ExistingOrderItemProductReview', id: `${customerId}-ORDERITEM-${orderItemId}` });
            tags.push({ type: 'UserReviewsGiven', id: customerId });
          }
        } else {
          // Even when no review exists, tag the pattern for cache invalidation
          tags.push({ type: 'ExistingOrderItemProductReview', id: `ORDERITEM-${orderItemId}` });
        }
        return tags;
      }
    }),
  }),
});

export const {
  useGetProductReviewsQuery,
  useGetReviewStatsQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  useDeleteReviewMutation,
  useGetUserReviewsGivenQuery,
  useGetUserReviewsReceivedQuery,
  useGetReviewByIdQuery,
  useGetMerchantReviewsQuery,
  useGetExistingCustomerReviewQuery,
  useGetExistingOrderCustomerReviewQuery,
  useGetExistingOrderItemProductReviewQuery,
} = enhancedReviewApi;